import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ProductGrid } from "../components/ProductCard";
import { api } from "../lib/api";
import { useCategories, useSeo } from "../lib/hooks";

const EMPTY = { volumes: [], families: [], categories: [], min: "", max: "", availability: "all" };
const SORTS = [["recent", "Nouveautés"], ["price_asc", "Prix croissant"], ["price_desc", "Prix décroissant"], ["best", "Meilleures ventes"], ["name", "Ordre alphabétique"]];

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const Group = ({ title, children }) => (
  <div className="py-6 border-b border-[#C9A45C]/10">
    <p className="eyebrow mb-4">{title}</p>
    <div className="space-y-3">{children}</div>
  </div>
);

const CheckRow = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-3 cursor-pointer text-sm text-[#F8F6F1]/80 hover:text-[#F8F6F1]">
    <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} className="border-[#C9A45C]/50 data-[state=checked]:bg-[#C9A45C] data-[state=checked]:text-[#0B0B0B]" />
    {label}
  </label>
);

const FiltersPanel = ({ f, setF, facets, cats, showCategory }) => (
  <div data-testid="filters-panel">
    {showCategory && (
      <Group title="Catégorie">
        {cats.map((c) => <CheckRow key={c.slug} label={c.name} checked={f.categories.includes(c.slug)} onChange={() => setF({ ...f, categories: toggle(f.categories, c.slug) })} testid={`filter-category-${c.slug}`} />)}
      </Group>
    )}
    <Group title="Prix (€)">
      <div className="flex items-center gap-3">
        <input type="number" min="0" inputMode="decimal" placeholder="Min" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })}
          className="w-full bg-transparent border border-[#C9A45C]/30 px-3 py-2 text-sm outline-none focus:border-[#C9A45C]" data-testid="filter-price-min" />
        <span className="text-[#F8F6F1]/40">—</span>
        <input type="number" min="0" inputMode="decimal" placeholder={facets.max_price ? `${Math.ceil(facets.max_price)}` : "Max"} value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })}
          className="w-full bg-transparent border border-[#C9A45C]/30 px-3 py-2 text-sm outline-none focus:border-[#C9A45C]" data-testid="filter-price-max" />
      </div>
    </Group>
    <Group title="Contenance">
      <div className="flex flex-wrap gap-2">
        {facets.volumes.map((v) => (
          <button key={v} onClick={() => setF({ ...f, volumes: toggle(f.volumes, v) })} data-testid={`filter-volume-${v}`}
            className={`px-3 py-1.5 text-xs border transition-colors duration-300 ${f.volumes.includes(v) ? "bg-[#C9A45C] text-[#0B0B0B] border-[#C9A45C]" : "border-[#C9A45C]/30 text-[#F8F6F1]/75 hover:border-[#C9A45C]"}`}>
            {v} ml
          </button>
        ))}
      </div>
    </Group>
    <Group title="Famille olfactive">
      {facets.families.map((fam) => <CheckRow key={fam} label={fam} checked={f.families.includes(fam)} onChange={() => setF({ ...f, families: toggle(f.families, fam) })} testid={`filter-family-${fam}`} />)}
    </Group>
    <Group title="Disponibilité">
      {[["all", "Tous les produits"], ["in", "En stock"], ["out", "Épuisés"]].map(([v, l]) => (
        <label key={v} className="flex items-center gap-3 cursor-pointer text-sm text-[#F8F6F1]/80">
          <input type="radio" name="availability" checked={f.availability === v} onChange={() => setF({ ...f, availability: v })} className="accent-[#C9A45C]" data-testid={`filter-availability-${v}`} />
          {l}
        </label>
      ))}
    </Group>
    <button onClick={() => setF(EMPTY)} className="mt-6 eyebrow !text-[#F8F6F1]/60 hover:!text-[#C9A45C]" data-testid="filters-reset">Réinitialiser les filtres</button>
  </div>
);

export default function Catalog() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const cats = useCategories();
  const [cat, setCat] = useState(null);
  const [facets, setFacets] = useState({ families: [], volumes: [], max_price: 0 });
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState(sp.get("q") || "");
  const [sort, setSort] = useState("recent");
  const [products, setProducts] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { api.get("/facets").then((r) => setFacets(r.data)); }, []);
  useEffect(() => { setQ(sp.get("q") || ""); }, [sp]);
  useEffect(() => {
    setF(EMPTY);
    setCat(null);
    if (slug) api.get(`/categories/${slug}`).then((r) => setCat(r.data)).catch(() => setCat(false));
  }, [slug]);

  const params = useMemo(() => {
    const p = { sort, limit: 200 };
    const catList = slug ? [slug] : f.categories;
    if (catList.length) p.category = catList.join(",");
    if (q.trim()) p.q = q.trim();
    if (f.min !== "") p.min_price = f.min;
    if (f.max !== "") p.max_price = f.max;
    if (f.volumes.length) p.volume = f.volumes.join(",");
    if (f.families.length) p.family = f.families.join(",");
    if (f.availability !== "all") p.available = f.availability;
    return p;
  }, [slug, f, q, sort]);

  useEffect(() => {
    const t = setTimeout(() => api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => setProducts([])), 250);
    return () => clearTimeout(t);
  }, [params]);

  useSeo({
    title: cat ? cat.seo_title || cat.name : "Boutique — tous nos parfums",
    description: cat ? cat.seo_description : "Tous les parfums NasDeal : homme, femme, mixtes, Dubaï, brumes, intérieur et muscs. Filtrez par prix, contenance et famille olfactive.",
    image: cat?.image,
  });

  if (cat === false) return <div className="max-w-7xl mx-auto px-5 py-32 text-center"><p className="font-display text-3xl">Catégorie introuvable</p><Link to="/boutique" className="btn-outline-gold mt-8">Retour à la boutique</Link></div>;

  const activeCount = f.volumes.length + f.families.length + f.categories.length + (f.min !== "") + (f.max !== "") + (f.availability !== "all");

  return (
    <div data-testid="catalog-page">
      <section className="relative overflow-hidden border-b border-[#C9A45C]/15">
        {cat?.image && <img src={cat.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] to-[#0B0B0B]/60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <nav className="text-[11px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-[#C9A45C]">Accueil</Link> / <Link to="/boutique" className="hover:text-[#C9A45C]">Boutique</Link>{cat && <> / <span className="text-[#C9A45C]">{cat.name}</span></>}
          </nav>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="catalog-title">{cat ? cat.name : "Tous nos parfums"}</h1>
          <p className="mt-4 max-w-xl text-[#F8F6F1]/65 text-sm sm:text-base">{cat ? cat.description : "Explorez l'ensemble de la sélection NasDeal."}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3"><FiltersPanel f={f} setF={setF} facets={facets} cats={cats} showCategory={!slug} /></aside>
        <div className="lg:col-span-9">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-8">
            <div className="flex-1 flex items-center gap-3 border border-[#C9A45C]/30 px-4 focus-within:border-[#C9A45C] transition-colors">
              <Search className="h-4 w-4 text-[#C9A45C]" />
              <input value={q} onChange={(e) => { setQ(e.target.value); if (sp.get("q")) setSp({}); }} placeholder="Rechercher un parfum…" className="flex-1 bg-transparent py-3 text-sm outline-none" data-testid="catalog-search-input" />
              {q && <button onClick={() => { setQ(""); setSp({}); }} aria-label="Effacer"><X className="h-4 w-4" /></button>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden flex-1 flex items-center justify-center gap-2 border border-[#C9A45C]/30 px-4 py-3 text-xs tracking-[0.2em] uppercase" data-testid="mobile-filters-button">
                <SlidersHorizontal className="h-4 w-4" /> Filtres {activeCount > 0 && `(${activeCount})`}
              </button>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="flex-1 sm:w-52 rounded-none border-[#C9A45C]/30 h-[46px] bg-transparent" data-testid="catalog-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#C9A45C]/30">
                  {SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-option-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase text-[#F8F6F1]/45 mb-6" data-testid="catalog-results-count">{products ? `${products.length} produit${products.length > 1 ? "s" : ""}` : "Chargement…"}</p>
          {products && products.length === 0 ? (
            <div className="border border-dashed border-[#C9A45C]/30 py-20 text-center" data-testid="catalog-empty">
              <p className="font-display text-2xl">Aucun parfum ne correspond à votre recherche.</p>
              <button onClick={() => { setF(EMPTY); setQ(""); setSp({}); }} className="btn-outline-gold mt-6">Réinitialiser</button>
            </div>
          ) : <ProductGrid products={products || []} testid="catalog-grid" />}
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="bottom" className="bg-[#0B0B0B] border-[#C9A45C]/20 max-h-[85vh] overflow-y-auto" data-testid="mobile-filters-sheet">
          <SheetTitle className="font-display text-2xl font-normal">Filtres</SheetTitle>
          <FiltersPanel f={f} setF={setF} facets={facets} cats={cats} showCategory={!slug} />
          <button onClick={() => setMobileOpen(false)} className="btn-gold w-full mt-6" data-testid="mobile-filters-apply">Voir {products?.length ?? 0} produit(s)</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
